import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { motion } from 'framer-motion';
import { FaChartBar, FaCalendarAlt, FaImage } from 'react-icons/fa';
import { getAuthHeaders } from '../utils/auth';
import { apiFetch } from '../utils/apiUtils';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const Usage = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [usageData, setUsageData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUsageData = async () => {
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const headers = await getAuthHeaders(getAccessTokenSilently);
        
        const data = await apiFetch('/api/usage/stats', {
          headers,
          credentials: 'include',
          mode: 'cors'
        });

        console.log('[RETRO-USAGE] 📊 Fetched usage data:', data);
        setUsageData(data);
        setError(null);
      } catch (err) {
        console.error('[RETRO-USAGE] ❌ Error fetching usage data:', err);
        setError('Failed to load usage statistics');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsageData();
  }, [isAuthenticated, getAccessTokenSilently]);

  // Format month for display
  const formatMonth = (monthString) => {
    const [year, month] = monthString.split('-');
    return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
  };

  // Get max count for scaling bars
  const maxCount = usageData?.monthlyData?.length > 0
    ? Math.max(...usageData.monthlyData.map(d => d.count))
    : 0;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8F7F4] pt-8">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center">
            <div className="inline-block animate-pulse">
              <FaChartBar className="text-6xl text-black/20 mb-4" />
            </div>
            <p className="font-['VT323'] text-2xl text-black/40">Loading usage data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8F7F4] pt-8">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <p className="font-['VT323'] text-2xl text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const hasData = usageData && usageData.monthlyData.length > 0;

  return (
    <div className="min-h-screen bg-[#F8F7F4] pt-8 pb-16">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="font-['VT323'] text-4xl md:text-6xl text-black/80 mb-4 flex items-center justify-center gap-3">
            <FaChartBar className="text-black/60" />
            Usage Statistics
          </h1>
          <p className="font-['VT323'] text-xl text-black/60 max-w-2xl mx-auto">
            Track your creative journey and see how many images you've generated each month
          </p>
        </motion.div>

        {/* Summary Cards */}
        {hasData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="grid md:grid-cols-3 gap-6 mb-12"
          >
            {/* Total Images */}
            <div className="bg-white/90 backdrop-blur-xl rounded-lg border border-black/[0.08] p-6">
              <div className="flex items-center gap-3 mb-2">
                <FaImage className="text-black/40 text-xl" />
                <h3 className="font-['VT323'] text-lg text-black/60">Total Images</h3>
              </div>
              <p className="font-['VT323'] text-5xl text-black/80">{usageData.totalImages}</p>
            </div>

            {/* Active Months */}
            <div className="bg-white/90 backdrop-blur-xl rounded-lg border border-black/[0.08] p-6">
              <div className="flex items-center gap-3 mb-2">
                <FaCalendarAlt className="text-black/40 text-xl" />
                <h3 className="font-['VT323'] text-lg text-black/60">Active Months</h3>
              </div>
              <p className="font-['VT323'] text-5xl text-black/80">
                {usageData.monthlyData.length}
              </p>
            </div>

            {/* Average per Month */}
            <div className="bg-white/90 backdrop-blur-xl rounded-lg border border-black/[0.08] p-6">
              <div className="flex items-center gap-3 mb-2">
                <FaChartBar className="text-black/40 text-xl" />
                <h3 className="font-['VT323'] text-lg text-black/60">Avg per Month</h3>
              </div>
              <p className="font-['VT323'] text-5xl text-black/80">
                {Math.round(usageData.totalImages / usageData.monthlyData.length)}
              </p>
            </div>
          </motion.div>
        )}

        {/* Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white/90 backdrop-blur-xl rounded-lg border border-black/[0.08] p-8"
        >
          <h2 className="font-['VT323'] text-2xl text-black/80 mb-8 text-center">
            Monthly Breakdown
          </h2>

          {!hasData ? (
            <div className="text-center py-12">
              <FaImage className="text-6xl text-black/20 mx-auto mb-4" />
              <p className="font-['VT323'] text-2xl text-black/40 mb-2">
                No images yet
              </p>
              <p className="font-['VT323'] text-lg text-black/30">
                Start creating to see your usage statistics!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {usageData.monthlyData.map((monthData, index) => {
                const percentage = (monthData.count / maxCount) * 100;
                const barColor = '#00FF9F'; // Using the signature green from the app

                return (
                  <motion.div
                    key={monthData.month}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="relative"
                  >
                    <div className="flex items-center gap-4">
                      {/* Month Label */}
                      <div className="w-24 text-right flex-shrink-0">
                        <span className="font-['VT323'] text-lg text-black/60">
                          {formatMonth(monthData.month)}
                        </span>
                      </div>

                      {/* Bar Container */}
                      <div className="flex-1 relative">
                        <div className="bg-black/[0.05] rounded-lg h-12 overflow-hidden relative">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${percentage}%` }}
                            transition={{ duration: 0.8, delay: index * 0.05, ease: "easeOut" }}
                            className="h-full rounded-lg relative"
                            style={{ 
                              backgroundColor: barColor,
                              minWidth: monthData.count > 0 ? '2%' : '0%'
                            }}
                          >
                            {/* Animated gradient overlay */}
                            <div 
                              className="absolute inset-0 opacity-20"
                              style={{
                                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)'
                              }}
                            />
                          </motion.div>
                        </div>
                      </div>

                      {/* Count Label */}
                      <div className="w-16 flex-shrink-0">
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.05 + 0.3 }}
                          className="font-['VT323'] text-2xl text-black/80 font-bold"
                        >
                          {monthData.count}
                        </motion.span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* Footer Info */}
        {hasData && usageData.firstImageDate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center mt-8"
          >
            <p className="font-['VT323'] text-lg text-black/40">
              Creating since {new Date(usageData.firstImageDate).toLocaleDateString('en-US', { 
                month: 'long', 
                year: 'numeric' 
              })}
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Usage;





























